import { ReminderListItem, ReminderStatus } from 'models/reminders'

/**
 * Calculate the end date based on number of occurrences for recurring reminders.
 * This is the TypeScript version of the Python RRuleGenerator.calculate_end_date_from_occurrences method.
 */
export function calculateEndDateFromOccurrences(
  scheduledDate: Date,
  reminderType: ReminderListItem['reminder_type'],
  occurrenceCount: number,
  customInterval?: number | null,
  customUnit?: 'days' | 'weeks' | 'months' | null,
  occurrencesCompleted: number = 0
): Date {
  // Calculate remaining occurrences
  const remainingOccurrences = occurrenceCount - occurrencesCompleted

  if (remainingOccurrences <= 0) {
    return scheduledDate
  }

  const endDate = new Date(scheduledDate)

  // Calculate based on reminder type using remaining occurrences
  if (reminderType === 'daily') {
    endDate.setDate(endDate.getDate() + (remainingOccurrences - 1))
  } else if (reminderType === 'weekly') {
    endDate.setDate(endDate.getDate() + (remainingOccurrences - 1) * 7)
  } else if (reminderType === 'monthly') {
    endDate.setMonth(endDate.getMonth() + (remainingOccurrences - 1))
  } else if (reminderType === 'quarterly') {
    endDate.setMonth(endDate.getMonth() + (remainingOccurrences - 1) * 3)
  } else if (reminderType === 'annually') {
    endDate.setFullYear(endDate.getFullYear() + (remainingOccurrences - 1))
  } else if (reminderType === 'custom') {
    if (!customInterval || !customUnit) {
      throw new Error('Custom interval and unit required for custom recurrence')
    }

    // Calculate based on custom settings using remaining occurrences
    // For custom intervals, we multiply remaining_occurrences by custom_interval
    // Example: 2 remaining occurrences every 2 days = 2 * 2 = 4 days from start
    const totalIntervals = remainingOccurrences * customInterval

    if (customUnit === 'days') {
      endDate.setDate(endDate.getDate() + totalIntervals)
    } else if (customUnit === 'weeks') {
      endDate.setDate(endDate.getDate() + totalIntervals * 7)
    } else if (customUnit === 'months') {
      endDate.setMonth(endDate.getMonth() + totalIntervals)
    } else {
      throw new Error(`Invalid custom unit: ${customUnit}`)
    }
  }

  return endDate
}

export const statusColors: Record<ReminderStatus, string> = {
  scheduled: 'bg-purple-100 text-purple-700',
  sent: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  deleted: 'bg-gray-300 text-gray-700',
  active: 'bg-lime-100 text-lime-700',
  paused: 'bg-yellow-100 text-yellow-700',
  ended: 'bg-orange-100 text-orange-700',
}

export function reminderTypeFormat(reminder: ReminderListItem) {
  if (reminder.reminder_type === 'one_off') {
    return 'One-time'
  }

  if (reminder.reminder_type === 'custom') {
    return `Every ${reminder.custom_interval} ${reminder.custom_unit}`
  }

  return (
    reminder.reminder_type.charAt(0).toUpperCase() +
    reminder.reminder_type.slice(1)
  )
}
